import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { approvalsApi, vendorsApi } from '../lib/api';
import { ROLE_LABELS } from '../types';
import type { ApprovalStep, VendorRegistryItem } from '../types';
import AppShell from '../components/AppShell';

export default function ApprovalInboxPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<ApprovalStep[]>([]);
  const [vendors, setVendors] = useState<VendorRegistryItem[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      approvalsApi.inbox(),
      vendorsApi.registry().catch(() => [] as VendorRegistryItem[]),
    ])
      .then(([inbox, reg]) => {
        setSteps(inbox);
        setVendors(reg);
      })
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  }, [user]);

  if (loading || !user) return null;

  const vendorMap = new Map<string, VendorRegistryItem>();
  for (const v of vendors) vendorMap.set(v.id, v);

  const subtitle = `${ROLE_LABELS[user.role]} · ${steps.length} pending decision${steps.length !== 1 ? 's' : ''}`;

  return (
    <AppShell user={user} title="Approval Inbox" subtitle={subtitle}>
      {error && <ErrorBanner message={error} />}

      {fetching ? (
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</p>
      ) : steps.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '4rem 2rem',
          backgroundColor: '#ffffff', border: '1px dashed #cbd5e1', borderRadius: 10,
        }}>
          <p style={{ fontSize: '1rem', color: '#64748b', margin: 0 }}>You have no pending approvals.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {steps.map((step) => (
            <InboxCard
              key={step.id}
              step={step}
              vendorBenchmark={step.purchaseRequest?.vendorId ? vendorMap.get(step.purchaseRequest.vendorId) ?? null : null}
              onClick={() => navigate(`/purchase-requests/${step.purchaseRequestId}`)}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function InboxCard({
  step,
  vendorBenchmark,
  onClick,
}: {
  step: ApprovalStep;
  vendorBenchmark: VendorRegistryItem | null;
  onClick: () => void;
}) {
  const pr = step.purchaseRequest;
  if (!pr) return null;

  const amount = parseFloat(pr.amount).toLocaleString('en-US', { style: 'currency', currency: pr.currency });
  const vendorName = pr.vendor?.name ?? (pr.metadata?.vendorName as string) ?? 'No vendor';
  const ago = timeSince(step.createdAt);

  const bm = vendorBenchmark?.benchmark;
  const overpayment = bm?.overpaymentPct ?? null;
  const savings = bm?.adjustedSavingsUsd ?? bm?.savingsPotentialUsd ?? null;
  const showBenchmarkAlert = overpayment != null && overpayment > 0 && savings != null && savings > 0;

  return (
    <div style={{
      borderRadius: 10, overflow: 'hidden',
      border: '1px solid #e2e8f0',
      borderLeft: '3px solid #0ea5e9',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {showBenchmarkAlert && (
        <div style={{
          backgroundColor: '#fef3c7', borderBottom: '1px solid rgba(245,158,11,0.3)',
          padding: '0.6rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <span style={{ fontSize: '0.82rem' }}>⚠</span>
          <span style={{ fontSize: '0.8rem', color: '#92400e' }}>
            <strong>{vendorName}</strong> is {overpayment!.toFixed(0)}% above market rate
            {' '}— <strong>{formatCurrencyLarge(savings!)}</strong> savings identified by SpendLens benchmarking
          </span>
        </div>
      )}

      <div
        onClick={onClick}
        style={{
          backgroundColor: '#ffffff', padding: '1.25rem 1.5rem', cursor: 'pointer',
          transition: 'background-color 0.1s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 400, color: '#0f172a' }}>{pr.title}</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
              Step {step.stepOrder} · {ago}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
            <span style={{ fontSize: '1rem', fontWeight: 400, color: '#0f172a' }}>{amount}</span>
            <span style={{
              fontSize: '0.72rem', fontWeight: 400,
              background: '#fef3c7', color: '#92400e',
              borderRadius: 20, padding: '0.2rem 0.7rem',
            }}>
              Pending
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.6rem 1.5rem' }}>
          <DetailField label="Vendor" value={vendorName} />
          <DetailField label="Requester" value={pr.requester?.name ?? '—'} />
          <DetailField label="Department" value={pr.department?.name ?? '—'} />
          <DetailField label="Type" value={pr.type.replace(/_/g, ' ')} />
          {pr.spendCategory && <DetailField label="Category" value={pr.spendCategory} />}
          {pr.seats && <DetailField label="Seats" value={String(pr.seats)} />}
        </div>

        {pr.businessCase && (
          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Business Case</p>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: 1.55 }}>{pr.businessCase}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p style={{ margin: '0 0 0.1rem', fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569' }}>{value}</p>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1rem',
    }}>
      {message}
    </div>
  );
}

function timeSince(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatCurrencyLarge(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
